import React, { useLayoutEffect, useRef } from 'react'

// Content never shrinks below half size — past that it is clipped instead of becoming unreadable.
export const MIN_FIT_SCALE = 0.5
const SEARCH_STEPS = 8

/**
 * Find the largest zoom (between MIN_FIT_SCALE and 1) at which `content` is no taller than `container`,
 * and apply it. Zoom reflows the content (e.g. more cards per row as they shrink), so the height isn't
 * linear in the scale — binary search rather than a single ratio.
 */
export const applyFitScale = (container: HTMLElement, content: HTMLElement): number => {
  const available = container.clientHeight
  const fits = (scale: number): boolean => {
    content.style.zoom = String(scale)
    return content.getBoundingClientRect().height <= available + 0.5
  }

  if (fits(1)) return 1

  let low = MIN_FIT_SCALE
  let high = 1
  for (let step = 0; step < SEARCH_STEPS; step++) {
    const mid = (low + high) / 2
    if (fits(mid)) low = mid
    else high = mid
  }
  content.style.zoom = String(low)
  return low
}

interface ScaleToFitProps {
  children: React.ReactNode
}

/**
 * Shrinks its children so they fit the height of the parent with no scrollbar. Anything still too tall at
 * MIN_FIT_SCALE is clipped. Content shorter than the parent stays at full size, vertically centred.
 */
const ScaleToFit: React.FC<ScaleToFitProps> = ({ children }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const container = containerRef.current
    const content = contentRef.current
    if (!container || !content) return

    const refit = () => applyFitScale(container, content)
    refit()

    // Re-fit when the insert is resized or the content changes size (data loads, settings change).
    // A refit lands on the same size for the same content, so this settles after one pass.
    const observer = new ResizeObserver(refit)
    observer.observe(container)
    observer.observe(content)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={containerRef} data-testid="scale-to-fit" className="flex flex-col w-full h-full overflow-hidden">
      {/* my-auto centres short content; auto margins collapse to 0 on overflow so clipping starts at the top. */}
      <div ref={contentRef} data-testid="scale-to-fit-content" className="w-full shrink-0 my-auto flow-root">
        {children}
      </div>
    </div>
  )
}

export default ScaleToFit
